/**
 * Inline recruiter-note widget for AI Analysis subitems.
 *
 * Use cases:
 *   <AnalysisNote applicationId={aid} block="strengths" itemKey="0" notes={notes} onChange={refresh} />
 *
 * • Tiny "+ Add note" pill button next to each subitem
 * • Expands to a textarea inline; Save/Delete buttons; Esc cancels
 * • When a note already exists, renders the note inline with "Recruiter note (added by X on …)"
 *   and a pencil icon to edit
 *
 * Special itemKey="__section__" => the free-form per-section recruiter note
 * rendered at the bottom of each Block.
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, Pencil, Trash2, Save, X } from "lucide-react";

function shortDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

export default function AnalysisNote({ applicationId, block, itemKey, notes, onChange, compact = false }) {
    const key = `${block}:${itemKey}`;
    const existing = (notes || {})[key];
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(existing?.text || "");
    const [busy, setBusy] = useState(false);

    const open = () => { setText(existing?.text || ""); setEditing(true); };
    const close = () => { setEditing(false); setText(existing?.text || ""); };

    const save = async () => {
        const trimmed = text.trim();
        setBusy(true);
        try {
            await api.post(`/applications/${applicationId}/analysis-notes`, { block, item_key: itemKey, text: trimmed });
            toast.success(trimmed ? "Note saved" : "Note removed");
            setEditing(false);
            onChange?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm("Delete this note?")) return;
        setBusy(true);
        try {
            await api.delete(`/applications/${applicationId}/analysis-notes`, { params: { block, item_key: itemKey } });
            toast.success("Note deleted");
            setEditing(false);
            setText("");
            onChange?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (editing) {
        return (
            <div className="mt-1.5 ml-5 border border-ikb/40 bg-blue-50/30 p-2 space-y-1.5" data-testid={`an-edit-${key}`}>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Recruiter note (visible to your team)…"
                    rows={3}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Escape") close(); }}
                    className="w-full text-[12px] border border-border bg-white p-1.5 rounded-none outline-none focus:border-ikb"
                    data-testid={`an-textarea-${key}`}
                />
                <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={save} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`an-save-${key}`}>
                        <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
                    </button>
                    <button onClick={close} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1" data-testid={`an-cancel-${key}`}>
                        <X className="w-3 h-3" /> Cancel
                    </button>
                    {existing && (
                        <button onClick={remove} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest border border-rose-300 text-rose-700 bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-rose-50" data-testid={`an-delete-${key}`}>
                            <Trash2 className="w-3 h-3" /> Delete
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (existing) {
        return (
            <div className={`mt-1.5 ${compact ? "" : "ml-5"} border-l-2 border-ikb bg-blue-50/40 px-2.5 py-1.5 text-[12px]`} data-testid={`an-view-${key}`}>
                <div className="whitespace-pre-wrap">{existing.text}</div>
                <div className="text-[10px] font-mono text-ikb/70 mt-1 inline-flex items-center gap-1.5 flex-wrap">
                    <span>Recruiter note · {existing.author_name || "Recruiter"} · {shortDate(existing.updated_at || existing.created_at)}</span>
                    <button onClick={open} className="ml-1 inline-flex items-center gap-0.5 hover:text-ikb underline" data-testid={`an-edit-btn-${key}`}>
                        <Pencil className="w-2.5 h-2.5" /> Edit
                    </button>
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={open}
            className={`${compact ? "" : "ml-5"} mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-ikb/70 hover:text-ikb hover:underline`}
            data-testid={`an-add-${key}`}
        >
            <MessageSquarePlus className="w-3 h-3" /> Add note
        </button>
    );
}
